(() => {
  if (customElements.get("kitsune-catalog")) return;
  const escape = (value) =>
    String(value).replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );
  const money = (cents) =>
    new Intl.NumberFormat("pl-PL", {
      style: "currency",
      currency: "PLN",
    }).format(cents / 100);
  const caret = `<svg class="kf-caret" viewBox="0 0 12 8" aria-hidden="true" focusable="false"><path d="m1 1 5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>`;
  const keys = [
    "vendor",
    "type",
    "brand",
    "model",
    "generation",
    "min",
    "max",
    "q",
    "sort",
    "page",
  ];
  class Catalog extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.hiddenNative = new Map();
      this.sequence = 0;
      this.onPop = () => {
        this.loadState();
        this.load();
      };
      this.loadState();
      window.addEventListener("popstate", this.onPop);
      this.addEventListener("change", (e) => this.changed(e));
      this.addEventListener("submit", (e) => this.submit(e));
      this.addEventListener("click", (e) => this.clicked(e));
      this.addEventListener("input", (e) => this.findValues(e));
      this.load();
    }
    disconnectedCallback() {
      window.removeEventListener("popstate", this.onPop);
      this.abort?.abort();
      this.restore();
    }
    loadState() {
      const url = new URL(location.href);
      this.params = new URLSearchParams();
      for (const key of keys)
        for (const v of url.searchParams.getAll("kf." + key))
          this.params.append(key, v);
    }
    urlState() {
      const url = new URL(location.href);
      for (const key of keys) url.searchParams.delete("kf." + key);
      for (const [key, value] of this.params)
        url.searchParams.append("kf." + key, value);
      history.pushState(null, "", url);
    }
    async load() {
      const sequence = ++this.sequence;
      this.abort?.abort();
      this.abort = new AbortController();
      this.setAttribute("aria-busy", "true");
      const focus = document.activeElement?.id;
      try {
        if (
          this.dataset.country !== "PL" ||
          this.dataset.currency !== "PLN" ||
          this.dataset.language !== "pl"
        )
          throw new Error(
            "Filtry są dostępne dla polskiego rynku i cen w PLN.",
          );
        const url = new URL(this.dataset.api, location.origin);
        if (url.origin !== location.origin)
          throw new Error("Nieprawidłowy adres filtrów.");
        url.search = new URLSearchParams(this.params).toString();
        url.searchParams.set("collection", this.dataset.collection || "all");
        if (this.dataset.collection === "all") url.searchParams.set("scope", "catalog");
        for (const k of ["country", "currency", "language"])
          url.searchParams.set(k, this.dataset[k]);
        if (this.dataset.cardsSection) url.searchParams.set("pageSize", "18");
        let data;
        if (this.dataset.liveSection) {
          if (!window.KitsuneThemeLiveSearch) throw new Error("Filtry nie są jeszcze gotowe.");
          data = await window.KitsuneThemeLiveSearch(this, url.searchParams);
        } else if (this.dataset.indexUrls) {
          if (!window.KitsuneThemePreviewSearch) throw new Error("Podgląd filtrów nie jest jeszcze gotowy.");
          data = await window.KitsuneThemePreviewSearch(this, url.searchParams);
        } else {
        const res = await fetch(url, {
          signal: this.abort.signal,
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          let body;
          try {
            body = await res.json();
          } catch {
            body = null;
          }
          throw new Error(body?.error || "Nie udało się wczytać filtrów.");
        }
        data = await res.json();
        }
        const nativeCards = this.dataset.cardsSection ? await this.loadNativeCards(data.products) : null;
        if (sequence !== this.sequence) return;
        this.render(data, nativeCards);
        this.hideNative();
        if (focus)
          this.querySelector("#" + CSS.escape(focus))?.focus({
            preventScroll: true,
          });
      } catch (e) {
        if (e.name === "AbortError" || sequence !== this.sequence) return;
        this.restore();
        this.innerHTML = `<div class="kf-error" role="alert"><p>${escape(e.message)}</p><button type="button" data-retry>Spróbuj ponownie</button><button type="button" data-clear>Wyczyść filtry</button><p>Standardowa lista produktów pozostaje dostępna poniżej.</p></div>`;
      } finally {
        if (sequence === this.sequence) this.removeAttribute("aria-busy");
      }
    }
    hideNative() {
      for (const selector of [
        this.dataset.nativeGrid,
        this.dataset.nativeFilters,
      ].filter(Boolean)) {
        let element;
        try {
          element = document.querySelector(selector);
        } catch {
          continue;
        }
        if (!element || element.contains(this) || this.contains(element))
          continue;
        if (!this.hiddenNative.has(element))
          this.hiddenNative.set(element, element.hidden);
        element.hidden = true;
      }
    }
    restore() {
      for (const [element, hidden] of this.hiddenNative)
        element.hidden = hidden;
      this.hiddenNative.clear();
    }
    changed(e) {
      const input = e.target;
      if (input.closest("[data-kf-native-cards]")) return;
      if (input.matches("[data-facet]")) {
        const key = input.name;
        const values = this.params.getAll(key).filter((v) => v !== input.value);
        this.params.delete(key);
        if (input.checked) values.push(input.value);
        for (const v of values) this.params.append(key, v);
      } else if (input.matches('[name="sort"]'))
        this.params.set("sort", input.value);
      else return;
      this.params.delete("page");
      this.urlState();
      this.load();
    }
    submit(e) {
      if (!e.target.matches(".kf-search, .kf-price-form")) return;
      e.preventDefault();
      const data = new FormData(e.target);
      for (const key of ["q", "min", "max"])
        if (data.has(key)) {
          const value = String(data.get(key)).trim();
          value ? this.params.set(key, value) : this.params.delete(key);
        }
      this.params.delete("page");
      this.urlState();
      this.load();
    }
    clicked(e) {
      if (e.target.closest("[data-kf-native-cards]")) return;
      const toggle = e.target.closest("[data-toggle]");
      if (toggle) {
        this.mobileOpen = !this.mobileOpen;
        this.classList.toggle("kf-mobile-open", this.mobileOpen);
        toggle.setAttribute("aria-expanded", String(this.mobileOpen));
        toggle.textContent = this.mobileOpen ? "Ukryj filtry" : "Pokaż filtry";
        return;
      }
      if (e.target.closest("[data-retry]")) return this.load();
      if (e.target.closest("[data-clear]")) {
        this.params = new URLSearchParams();
        this.urlState();
        this.load();
        return;
      }
      const page = e.target.closest("[data-page]");
      if (page) {
        this.params.set("page", page.dataset.page);
        this.urlState();
        this.load();
        this.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    findValues(e) {
      if (!e.target.matches("[data-find]")) return;
      const needle = e.target.value.toLocaleLowerCase("pl");
      for (const row of e.target
        .closest("details")
        .querySelectorAll(".kf-option"))
        row.hidden = !row.dataset.label
          .toLocaleLowerCase("pl")
          .includes(needle);
    }
    async loadNativeCards(products) {
      if (!products.length) return "";
      const handles = products.map((p) => decodeURIComponent(p.url.split("/").pop()));
      if (handles.length > 18 || handles.some((h) => !/^[a-z0-9][a-z0-9-]*$/.test(h)))
        throw new Error("Nieprawidłowa lista produktów.");
      const url = new URL(this.dataset.searchUrl || "/search", location.origin);
      url.searchParams.set("section_id", this.dataset.cardsSection);
      url.searchParams.set("q", handles.join(","));
      url.searchParams.set("type", "product");
      // Preserve the unpublished theme when opening the preview without a cookie.
      const previewId = new URL(location.href).searchParams.get("preview_theme_id");
      if (previewId) url.searchParams.set("preview_theme_id", previewId);
      const response = await fetch(url, { signal: this.abort.signal });
      if (!response.ok) throw new Error("Nie udało się wczytać kart produktów motywu.");
      const doc = new DOMParser().parseFromString(await response.text(), "text/html");
      const grid = doc.querySelector("[data-kf-native-cards]");
      const rendered = grid ? Array.from(grid.children, (li) => li.dataset.handle) : [];
      if (rendered.length !== handles.length || rendered.some((h, i) => h !== handles[i]))
        throw new Error("Lista produktów zmieniła się. Odśwież indeks filtrów.");
      return grid.outerHTML;
    }
    render(data, nativeCards = null) {
      const uid = this.id || "kf-catalog";
      this.id = uid;
      const expanded = new Set(
        Array.from(
          this.querySelectorAll("details[open]"),
          (e) => e.dataset.key,
        ),
      );
      const wasRendered = this.rendered;
      this.rendered = true;
      const filters = data.facets
        .map(
          (
            f,
          ) => `<details data-key="${f.key}" ${!wasRendered || expanded.has(f.key) ? "open" : ""}><summary><span>${escape(f.label)}${f.values.some(v => v.selected) ? `<span class="kf-selected-count">(${f.values.filter(v => v.selected).length})</span>` : ""}</span>${caret}</summary>
        ${f.values.length > 12 ? `<label class="kf-find-label">Szukaj w filtrze<input type="search" data-find placeholder="Wpisz nazwę…" aria-label="Szukaj: ${escape(f.label)}"></label>` : ""}
        <div class="kf-options">${f.values.map((v, i) => `<label class="kf-option" data-label="${escape(v.value)}"><input id="${uid}-${f.key}-${i}" type="checkbox" data-facet name="${f.key}" value="${escape(v.value)}" ${v.selected ? "checked" : ""} ${!v.count && !v.selected ? "disabled" : ""}><span>${escape(v.value)}</span><span class="kf-count">${v.count}</span></label>`).join("")}</div></details>`,
        )
        .join("");
      const cards = data.products
        .map((p) => {
          const image =
            p.image && p.image.startsWith("https://cdn.shopify.com/")
              ? `<img src="${escape(p.image)}${p.image.includes("?") ? "&" : "?"}width=600" alt="${escape(p.imageAlt)}" loading="lazy" width="600" height="450">`
              : '<div class="kf-placeholder" aria-hidden="true">KITSUNE</div>';
          const href =
            this.dataset.preview === "true"
              ? "https://kitsune-performance.pl" + p.url
              : p.url;
          return `<article class="kf-card"><a href="${escape(href)}" ${this.dataset.preview === "true" ? 'target="_blank" rel="noopener"' : ""}>${image}<p class="kf-vendor">${escape(p.vendor)}</p><h3>${escape(p.title)}</h3><p class="kf-price">${p.minPrice === 0 ? "Cena na zapytanie" : (p.maxPrice > p.minPrice ? "Od " : "") + money(p.minPrice)}</p><span class="kf-product-link">Sprawdź produkt <span aria-hidden="true">↗</span></span></a></article>`;
        })
        .join("");
      this.innerHTML = `<div class="kf-layout"><aside aria-label="Filtry produktów"><button type="button" class="kf-toggle" data-toggle aria-expanded="${Boolean(this.mobileOpen)}">${this.mobileOpen ? "Ukryj filtry" : "Pokaż filtry"}</button><div class="kf-filter-body"><div class="kf-filter-head"><h2>Filtry</h2><button type="button" data-clear>Wyczyść</button></div>
        <form class="kf-search"><label for="${uid}-q">Szukaj w kolekcji</label><div><input id="${uid}-q" type="search" name="q" value="${escape(this.params.get("q") || "")}" placeholder="Nazwa części lub samochodu"><button type="submit" aria-label="Szukaj w kolekcji"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 5 5"/></svg></button></div></form>
        <details data-key="price" ${!wasRendered || expanded.has("price") ? "open" : ""}><summary><span>Cena</span>${caret}</summary><form class="kf-price-form"><label>Od<input inputmode="decimal" name="min" value="${escape(this.params.get("min") || "")}" aria-label="Cena od w złotych"></label><label>Do<input inputmode="decimal" name="max" value="${escape(this.params.get("max") || "")}" aria-label="Cena do w złotych"></label><button type="submit">Zastosuj</button></form><p class="kf-price-hint">Cena początkowa produktu w PLN</p></details>${filters}</div></aside>
        <section aria-label="Produkty"><div class="kf-toolbar"><h2 tabindex="-1" role="status" aria-live="polite">${new Intl.NumberFormat("pl-PL").format(data.total)} produktów</h2><label><span class="kf-sort-label">Sortuj według</span><svg class="kf-sort-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M7 4v16m-4-4 4 4 4-4M17 20V4m-4 4 4-4 4 4"/></svg><select name="sort" aria-label="Sortuj produkty">${[
          ["title-ascending", "Nazwa: A–Z"],
          ["title-descending", "Nazwa: Z–A"],
          ["price-ascending", "Cena: rosnąco"],
          ["price-descending", "Cena: malejąco"],
          ["created-descending", "Najnowsze"],
        ]
          .map(
            ([v, l]) =>
              `<option value="${v}" ${data.sort === v ? "selected" : ""}>${l}</option>`,
          )
          .join("")}</select></label></div>
        ${data.total ? (nativeCards ?? `<div class="kf-grid">${cards}</div>`) : '<div class="kf-empty"><h3>Brak pasujących produktów</h3><p>Zmień wybrane filtry lub usuń część warunków.</p><button type="button" data-clear>Wyczyść filtry</button></div>'}
        <nav class="kf-pagination" aria-label="Strony wyników"><button type="button" data-page="${data.page - 1}" ${data.page === 1 ? "disabled" : ""}>← Poprzednia</button><span>Strona ${data.page} z ${data.pages}</span><button type="button" data-page="${data.page + 1}" ${data.page >= data.pages ? "disabled" : ""}>Następna →</button></nav></section></div>`;
    }
  }
  customElements.define("kitsune-catalog", Catalog);
})();
